/* Device-only public book references. Account records and character photos never belong here. */
(() => {
  'use strict';

  function createStore({ shop, local, session, now = Date.now }) {
    const prefix = `wonderbook:favorites:v1:${shop}:`;
    const memory = new Map();
    const valid = (item) => item && item.version === 1 && typeof item.productId === 'string' && /^\d+$/.test(item.productId)
      && /^[a-z0-9][a-z0-9-]*$/.test(item.handle) && typeof item.title === 'string'
      && item.title.length > 0 && item.title.length <= 500 && typeof item.image === 'string'
      && Number.isFinite(item.updatedAt) && Number.isFinite(item.savedAt)
      && typeof item.removed === 'boolean';

    function read() {
      const records = new Map();
      for (const [storage, persistence] of [[local, 'device'], [session, 'tab']]) {
        try {
          if (!storage) continue;
          for (let index = 0; index < storage.length; index += 1) {
            const key = storage.key(index);
            if (!key || !key.startsWith(prefix)) continue;
            try {
              const item = JSON.parse(storage.getItem(key));
              if (!valid(item) || key !== prefix + item.productId) continue;
              const previous = records.get(item.productId);
              if (!previous || item.updatedAt > previous.updatedAt) records.set(item.productId, { ...item, persistence });
            } catch (_) { /* One damaged record must not hide the remaining books. */ }
          }
        } catch (_) { /* Storage may be blocked; keep the available in-page records. */ }
      }
      for (const [id, item] of memory) {
        if (!records.has(id) || item.updatedAt >= records.get(id).updatedAt) records.set(id, item);
      }
      return records;
    }

    function save(details, removed = false) {
      const previous = read().get(String(details.productId));
      const timestamp = Math.max(now(), (previous?.updatedAt || 0) + 1);
      const item = {
        version: 1,
        productId: String(details.productId),
        handle: details.handle,
        title: String(details.title || '').slice(0, 500),
        image: String(details.image || ''),
        savedAt: previous && !previous.removed ? previous.savedAt : timestamp,
        updatedAt: timestamp,
        removed
      };
      if (!valid(item)) throw new Error('This book could not be saved.');
      const key = prefix + item.productId;
      const value = JSON.stringify(item);
      for (const [storage, persistence] of [[local, 'device'], [session, 'tab']]) {
        try {
          if (!storage) continue;
          storage.setItem(key, value);
          // A successful write replaces any weaker in-memory copy.
          memory.delete(item.productId);
          if (persistence === 'device') {
            try { session?.removeItem(key); } catch (_) { /* Older backup is ignored by updatedAt. */ }
          }
          return { ...item, persistence };
        } catch (_) { /* Fall back to the tab, then the current page. */ }
      }
      const fallback = { ...item, persistence: 'page' };
      memory.set(item.productId, fallback);
      return fallback;
    }

    return {
      prefix,
      list: () => [...read().values()].filter((item) => !item.removed).sort((a, b) => b.savedAt - a.savedAt || a.productId.localeCompare(b.productId)),
      set: save,
      remove(id) {
        const previous = read().get(String(id));
        return previous ? save(previous, true) : null;
      },
      persistence() {
        const levels = [...read().values()].map((item) => item.persistence);
        return levels.includes('page') ? 'page' : levels.includes('tab') ? 'tab' : 'device';
      }
    };
  }

  if (typeof module === 'object' && module.exports) module.exports = { createStore };
  else window.WonderbookFavoriteStorage = { createStore };
})();
